<template>
  <div>
    <div v-if="spinners.contentIsLoading" class="text-center mt-5 mb-5">
      <b-spinner type="grow" variant="primary"/>
    </div>
    <b-row v-else class="card-body">
      <b-col cols="12">
        <b-alert :show="axError" variant="danger" class="text-center">
          {{ 'ax_import_error_alert' | translate('global') }}
        </b-alert>
      </b-col>
      <b-col cols="12">
        <b-card>
          <h4>{{ 'invoice' | translate('global') }}: {{ invoiceInfoData.number }}</h4>
          <blockquote class="card-blockquote mt-2">
            <div class="table-responsive">
              <table class="table table-sm center">
                <thead>
                <tr>
                  <th class="no-border-top">{{ 'supplier' | translate('global') }}</th>
                  <th class="no-border-top">{{ 'invoice_issue_at' | translate('global') }}</th>
                  <th class="no-border-top">{{ 'sum' | translate('global') }}</th>
                  <th class="no-border-top">{{ 'status' | translate('global')}}</th>
                  <th class="no-border-top">{{ 'structure' | translate('global')}}</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td class="border-r-3">{{ invoiceInfoData['supplier'] }}</td>
                  <td class="border-r-3">{{ invoiceInfoData['issueAt'] | formatDate() }}</td>
                  <td class="border-r-3">{{ invoiceInfoData['sum'] }}</td>
                  <td class="border-r-3">{{ invoiceInfoData['status'] }}</td>
                  <td class="border-r-3">
                    <span v-for="item in invoiceInfoData['structures']" :key="item.id">
                       {{ item.name }}; 
                    </span>
                   
                    </td>
                </tr>
                </tbody>
              </table>
            </div>
          </blockquote>

          <EmptyTable v-if="!tableItems.length"/>
          <b-table v-else class="table-sm text-center mt-3 mb-3" v-model="currentItems"
                   responsive striped :fields="tableFields" :items="tableItems">
            <template v-slot:options="row">
              <a @click="row.toggleDetails">
                <i v-if="row.detailsShowing" class="fa fa-chevron-up fa-lg fa-primary"/>
                <i v-else @click="onToggleDetails(row)" class="fa fa-chevron-down fa-lg fa-primary"/>
              </a>

              <router-link target="_blank" :to="{ name: 'OrderUpdate', params:{ id: row.item.raw.order_id }}">
                <i class="fa fa-share-square-o fa-lg fa-success ml-3"/>
              </router-link>
            </template>

            <template v-slot:row-details="row">
              <b-card class="mr-3 ml-3">
                <b-row>
                  <b-col cols="6" xl="3">
                    <b-form-group :label="'vat_group' | translate('global')" label-class="h-40-px">
                      <treeselect value-format="object"
                                  :class="{ 'treeselect-error' : errorMsg.products_taxes_classifier_id }"
                                  :placeholder=" 'select_vat_group' | translate('global') "
                                  :options="vatGroupOptions" 
                                  v-model="selectedVatGroup"
                                  :disabled="!actions.update_items"/>
                      <span class="error-text" v-if="errorMsg.products_taxes_classifier_id">
                        {{ errorMsg.products_taxes_classifier_id[0] }}
                      </span>
                    </b-form-group>
                  </b-col>

                  <b-col cols="6" xl="3">
                    <b-form-group :label="'contract_dimension' | translate('global')" label-class="h-40-px">
                      <treeselect value-format="object"
                                  :class="{ 'treeselect-error' : errorMsg.contracts_classifier_id }"
                                  :placeholder=" 'contract_dimension_placeholder' | translate('global') "
                                  :options="contractOptions" v-model="selectedContract"
                                  :disabled="!actions.update_items"/>
                      <span class="error-text" v-if="errorMsg.contracts_classifier_id">
                        {{ errorMsg.contracts_classifier_id[0] }}
                      </span>
                    </b-form-group>
                  </b-col>
                  
                  <b-col cols="6" xl="3">
                    <b-form-group :label="'cost_center_dimension' | translate('global')" label-class="h-40-px">
                      <treeselect value-format="object"
                                  :class="{ 'treeselect-error' : errorMsg.projects_classifier_id }"
                                  :placeholder=" 'cost_center_dimension_placeholder' | translate('global') "
                                  :options="costsCenterOptions" v-model="selectedCostsCenter"
                                  :disabled="!actions.update_items"/>
                      <span class="error-text" v-if="errorMsg.projects_classifier_id">
                        {{ errorMsg.projects_classifier_id[0] }}
                      </span>
                    </b-form-group>
                  </b-col>

                  <b-col cols="6" xl="3">
                    <b-form-group :label="'project_dimension' | translate('global')" label-class="h-40-px">
                      <treeselect value-format="object"
                                  :class="{ 'treeselect-error' : errorMsg.projects_classifier_id }"
                                  :placeholder=" 'project_dimension_placeholder' | translate('global') "
                                  :options="projectOptions" v-model="selectedProject"
                                  :disabled="!actions.update_items"/>
                      <span class="error-text" v-if="errorMsg.projects_classifier_id">
                        {{ errorMsg.projects_classifier_id[0] }}
                      </span>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div slot="footer" class="pull-right flex-container">
                  <b-form-checkbox id="to-all-rows" class="mt-2 mr-3" v-model="applyToAllRows" :disabled="!actions.update_items">
                    {{ 'apply_to_all_rows' | translate('global') }}
                  </b-form-checkbox>
                  <b-button type="submit" variant="success" :disabled="(spinners.isSaving && isDisabled) || !actions.update_items"
                            @click="onSave()">
                    <b-spinner v-if="spinners.isSaving" type="grow" small/>
                    {{ 'save' | translate('global') }}
                  </b-button>
                  <b-button @click="row.toggleDetails" type="reset" variant="secondary" class="ml-2">
                    {{ 'cancel' | translate('global') }}
                  </b-button>
                </div>
              </b-card>
            </template>
            <template v-slot:bottom-row="row" >
              <td colspan="13" class="border-top-black"/>
              <td colspan="1" class="border-top-black">
                <strong class="text-center">{{ 'total_sum' | translate('global') }}</strong>
              </td>
              <td colspan="1" class="border-top-black"><strong>{{ itemsTotalSum }}</strong></td>
              <td colspan="1" class="border-top-black"/>
            </template>
          </b-table>
          <PaymentDelay v-if="tableItems.length" class="pull-right"/>
          <div v-if="tableItems.length" slot="footer" class="flex-container pull-right">
            <Approve/>
            <Reject class="ml-2"/>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import forEach from 'lodash.foreach';
import VuexTypes from '@/store/types/index';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import EmptyTable from '@/views/sharedComponents/EmptyTableData';
import PaymentDelay from './PaymentDelay.vue';
import Approve from './PaymentApprove.vue';
import Reject from './PaymentReject.vue';

const InvoiceTypes = VuexTypes.invoices;
export default {
  mounted() {
    this.getData();
  },
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        contentIsLoading: true,
        isSaving: false,
      },
      axError: false,
      isDisabled: true,
      invoiceInfoData: {},
      applyToAllRows: false,
      activeItemId: null,
      tableFields: [
        { key: 'order_id', label: this.$t('invoice_order_id', 'global') },
        { key: 'ax_structure', label: this.$t('ax_structure', 'global') },
        { key: 'title', label: this.$t('title', 'global') },
        { key: 'unit_price', label: this.$t('unit_price', 'global') },
        { key: 'amount', label: this.$t('amount', 'global') },
        { key: 'vat_group', label: this.$t('vat_group', 'global') },
        { key: 'ax_store', label: this.$t('ax_store', 'global') },
        { key: 'ax_expenses_income_dimension', label: this.$t('ax_expenses_income_dimension', 'global') },
        { key: 'transport_dimension', label: this.$t('transport_dimension', 'global') },
        { key: 'contract_dimension', label: this.$t('contract_dimension', 'global') },
        { key: 'cost_center_dimension', label: this.$t('cost_center_dimension', 'global') },
        { key: 'project_dimension', label: this.$t('project_dimension', 'global') },
        { key: 'warehouse', label: this.$t('warehouse', 'global') },
        { key: 'delivery_transport_included', label: this.$t('transport_included', 'global') },
        { key: 'sum', label: this.$t('sum', 'global') },
        { key: 'options', label: this.$t('options', 'global') },
      ],
      tableItems: [],
      itemsTotalSum: 0,
      currentItems: [],
      selectedVatGroup: null,
      selectedContract: null,
      selectedProject: null,
      selectedCostsCenter: null,
      vatGroupOptions: [],
      contractOptions: [],
      projectOptions: [],
      costsCenterOptions: [],
      errorMsg: {},
      actions: {},
    };
  },
  components: { Treeselect, EmptyTable, PaymentDelay, Approve, Reject },
  computed: {
    ...mapGetters('Invoices', [
      'invoiceUpdateData',
      'invoiceItemsList',
      'invoiceItemsUpdate',
      'invoiceClassifiers',
      'invoicePaymentApprove',
      'invoicePaymentReject',
    ]),
  },
  watch: {
    invoiceItemsList: {
      immediate: true,
      handler: function () {
        if (this.invoiceItemsList.status === statuses.LOADED) {
          this.spinners.contentIsLoading = false;
          this.fillData();
        } else if (this.invoiceItemsList.status === statuses.LOADING
          || this.invoiceItemsList.status === statuses.EMPTY) {
          this.spinners.contentIsLoading = true;
        }
      },
      deep: true
    },
    invoiceClassifiers: {
      immediate: true,
      handler: function () {
        if (this.invoiceClassifiers.status === statuses.LOADED) {
          this.fillClassifiers();
        }
      },
      deep: true
    },
    invoiceItemsUpdate: {
      handler: function () {
        this.errorMsg = {};
        this.spinners.isSaving = false;
        if (this.invoiceItemsUpdate.status === statuses.LOADED) {
          this.alertSuccess();
          this.getData();
        } else if (this.invoiceItemsUpdate.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.invoiceItemsUpdate.data.data.errors;
        }
      },
      deep: true
    },
    invoiceUpdateData: {
      immediate: true,
      handler: function () {
        if (this.invoiceUpdateData.status === statuses.LOADED) {
          this.fillInvoiceInfo();
          const actions = this.invoiceUpdateData.data['invoice']['actions']
            ? this.invoiceUpdateData.data['invoice']['actions'] : {};
          this.isDisabled = !actions['approve_payment'];
        }
      },
      deep: true
    },
    invoicePaymentApprove: {
      handler: function () {
        if (this.invoicePaymentApprove.status === statuses.LOADED) {
          if (this.invoicePaymentApprove.data['ax_import_error']) {
            this.axError = true;
            this.alertError();
          } else {
            this.axError = false;
            this.$emit('getData');
            this.alertSuccess();
          }
        } else if (this.invoicePaymentApprove.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
    invoicePaymentReject: {
      handler: function () {
        if (this.invoicePaymentReject.status === statuses.LOADED) {
          if (this.invoicePaymentReject.data['ax_import_error']) {
            this.axError = true;
            this.alertError();
          } else {
            this.axError = false;
            this.alertSuccess();
          }
        } else if (this.invoicePaymentReject.status === statuses.ERROR) {
          this.alertError();
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions('Invoices', [
      InvoiceTypes.INVOICE_ITEMS_LIST,
      InvoiceTypes.INVOICE_ITEMS_UPDATE,
    ]),
    getData() {
      this.INVOICE_ITEMS_LIST(this.$route.params.id);
    },
    onSave() {
      this.spinners.isSaving = true;
      const data = {
        invoiceId: this.$route.params.id,
        itemId: this.activeItemId,
        updateData: {
          products_taxes_classifier_id: this.selectedVatGroup ? this.selectedVatGroup['id'] : null,
          contracts_classifier_id: this.selectedContract ? this.selectedContract['id'] : null,
          projects_classifier_id: this.selectedProject ? this.selectedProject['id'] : null,
          costs_centers_classifier_id: this.selectedCostsCenter ? this.selectedCostsCenter['id'] : null,
        }
      };

      if (this.applyToAllRows) {
        data['updateData']['update_all'] = 1;
      }
      this.INVOICE_ITEMS_UPDATE(data);
    },
    fillData() {
      const result = [];
      let totalSum = 0;
      forEach(this.invoiceItemsList.data.data, (item) => {
        result.push({
          raw: item,
          id: item['id'],
          order_id: item['order_id'],
          title: item['product'] ? item['product']['full_name'] : '-',
          unit_price: item['fact_price'],
          amount: item['fact_quantity'],
          sum: item['total_fact'],
          vat_group: item['products_taxes_classifier'] ? item['products_taxes_classifier']['name'] : '-',
          ax_store: item['product'] ? item['product']['ax_product'] ? item['product']['ax_product']['ax_code']
            : (item['product']['ax_code'] ? item['product']['ax_code'] : '-')
            : '-',
          ax_expenses_income_dimension: item['product']['expenses_income_classifier']
            ? item['product']['expenses_income_classifier']['ax_code'] : '-',
          transport_dimension: item['transports_classifier'] ? item['transports_classifier']['ax_code'] : '-',
          contract_dimension: item['contracts_classifier'] ? item['contracts_classifier']['name'] : '-',
          project_dimension: item['projects_classifier'] ? item['projects_classifier']['name'] : '-',
          warehouse: item['warehouses_classifier'] ? item['warehouses_classifier']['name'] : '-',
          delivery_transport_included: item['is_delivery_transport']
            ? this.$t('yes', 'global') : this.$t('no', 'global'),
          ax_structure: item['order']['structure']['departments_classifier'] ? item['order']['structure']['departments_classifier']['name'] + ' (' + item['order']['structure']['departments_classifier']['ax_code'] + ')' : '-',
          cost_center_dimension: item['costs_centers_classifier'] ? item['costs_centers_classifier']['name'] + ' (' + item['costs_centers_classifier']['ax_code'] + ')': '-',
        });

        totalSum += Number(item['total_fact']);
      });
      this.tableItems = result;
      this.itemsTotalSum = totalSum.toFixed(2);
    },
    fillClassifiers() {
      this.contractOptions = this.fillTreeselectDropdown(
        this.invoiceClassifiers.data['contractsClassifier'], 'id', 'ax_code'
      );
      this.projectOptions = this.fillTreeselectDropdown(
        this.invoiceClassifiers.data['projectsClassifier'], 'id', 'ax_code'
      );
      this.vatGroupOptions = this.fillTreeselectDropdown(
        this.invoiceClassifiers.data['productsTaxesClassifier'], 'id', 'ax_code'
      );
    },
    fillInvoiceInfo() {
      const invoice = this.invoiceUpdateData.data ? this.invoiceUpdateData.data.invoice : {};
      this.actions = invoice['actions'] ? invoice['actions'] : {};
      this.invoiceInfoData = {
        number: invoice['number'],
        supplier: invoice['supplier'] ? invoice['supplier']['name'] : '-',
        issueAt: invoice['issue_at'],
        sum: invoice['sum'],
        status: this.$t(invoice['status'], 'statuses'),
        structures: invoice['structures']
      };
    },
    onToggleDetails(row) {
      this.currentItems.forEach((item) => {
        if (item.id !== row.item.id && item['_showDetails']) {
          Vue.set(item, '_showDetails', false);
        }
      });
      this.activeItemId = row['item']['id'];
      this.applyToAllRows = false;
      this.selectedContract = row['item']['raw']['contracts_classifier'] ? {
        id: row['item']['raw']['contracts_classifier']['id'],
        label: row['item']['raw']['contracts_classifier']['ax_code']
      } : null;

      this.selectedProject = row['item']['raw']['projects_classifier'] ? {
        id: row['item']['raw']['projects_classifier']['id'],
        label: row['item']['raw']['projects_classifier']['ax_code']
      } : null;

      this.selectedVatGroup = row['item']['raw']['products_taxes_classifier'] ? {
        id: row['item']['raw']['products_taxes_classifier']['id'],
        label: row['item']['raw']['products_taxes_classifier']['ax_code']
      } : null;

      this.selectedCostsCenter = row['item']['raw']['costs_centers_classifier'] ? {
        id: row['item']['raw']['costs_centers_classifier']['id'],
        label: row['item']['raw']['costs_centers_classifier']['name'] + ' (' + row['item']['raw']['costs_centers_classifier']['ax_code'] + ')'
      } : null;

      this.costsCenterOptions = [];
      if(row['item']['raw']['order']['structure']['departments_classifier']){
          var departamentAxCode = row['item']['raw']['order']['structure']['departments_classifier']['ax_code'];
          if(this.invoiceClassifiers.data['costsCentersClassifier'][departamentAxCode]){
            this.costsCenterOptions = this.fillTreeselectDropdown(
              this.invoiceClassifiers.data['costsCentersClassifier'][departamentAxCode], 'id', 'name'
            );
          }
      }

    }
  }
};
</script>

<style scoped>
  .border-top-black {
    border-top: 2px solid #c8ced3 !important;
  }
</style>
